import React from 'react';
import {
  Bell,
  Clock,
  UserPlus,
  ArrowRight,
  AlertTriangle,
  FileCheck,
  Settings
} from 'lucide-react';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  entityType?: string;
  entityId?: string;
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'TASK_DUE':
    case 'TASK_OVERDUE':
      return <Clock className="h-4 w-4 text-orange-600" />;
    case 'LEAD_ASSIGNED':
      return <UserPlus className="h-4 w-4 text-blue-600" />;
    case 'LEAD_STAGE_CHANGED':
      return <ArrowRight className="h-4 w-4 text-green-600" />;
    case 'DEAL_ROTTING':
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    case 'IMPORT_COMPLETE':
      return <FileCheck className="h-4 w-4 text-purple-600" />;
    case 'SYSTEM_ALERT':
      return <Settings className="h-4 w-4 text-red-600" />;
    default:
      return <Bell className="h-4 w-4 text-gray-600" />;
  }
}

function getIconBgColor(type: string) {
  switch (type) {
    case 'TASK_DUE':
    case 'TASK_OVERDUE':
      return 'bg-orange-100';
    case 'LEAD_ASSIGNED':
      return 'bg-blue-100';
    case 'LEAD_STAGE_CHANGED':
      return 'bg-green-100';
    case 'DEAL_ROTTING':
      return 'bg-yellow-100';
    case 'IMPORT_COMPLETE':
      return 'bg-purple-100';
    case 'SYSTEM_ALERT':
      return 'bg-red-100';
    default:
      return 'bg-gray-100';
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const icon = getNotificationIcon(notification.type);
  const timeAgo = formatTimeAgo(notification.createdAt);

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 text-left hover:bg-gray-50 flex items-start gap-3 transition-colors ${
        !notification.isRead ? 'bg-blue-50' : ''
      }`}
    >
      <div className={`p-2 rounded-full flex-shrink-0 ${getIconBgColor(notification.type)}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 truncate">{notification.title}</p>
        <p className="text-sm text-gray-600 truncate">{notification.message}</p>
        <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
      </div>
      {!notification.isRead && (
        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
      )}
    </button>
  );
};
